/**
 * «Olvidé mi contraseña», desde la pantalla de ingreso.
 *
 * La decision que gobierna esta pantalla: **el resultado es siempre el mismo**,
 * exista o no el email. No es descuido de la UI, es lo que el backend garantiza
 * (`api_acceso.MENSAJE_OLVIDO`): si dijera «ese email no existe», cualquiera
 * podria averiguar quien tiene cuenta probando direcciones de a una.
 *
 * La contracara honesta: cuando el despliegue todavia no tiene servidor de
 * correo, el mensaje lo dice y manda a la persona por el camino que SI funciona
 * —pedirle al administrador que le genere el enlace desde el panel—. Prometer
 * un mail que nunca va a salir es peor que no ofrecer la opcion.
 */

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/shell/primitives';
import { AccesoError, pedirRecuperacion } from '@/views/acceso/api';
import { Aviso, MarcoPublico } from '@/views/acceso/piezas';

export function RecuperarScreen() {
  const [email, setEmail] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState<{ mensaje: string; hayCorreo: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      const respuesta = await pedirRecuperacion(email.trim());
      setEnviado({ mensaje: respuesta.detail, hayCorreo: respuesta.email_configured });
    } catch (fallo) {
      // El unico error posible acá es el freno por demasiados pedidos, que NO
      // depende del email: no revela nada.
      setError(fallo instanceof AccesoError ? fallo.message : 'No se pudo procesar el pedido.');
    } finally {
      setEnviando(false);
    }
  }

  if (enviado !== null) {
    return (
      <MarcoPublico>
        <h1 className="text-xl font-semibold tracking-tight">Listo</h1>
        <div className="mt-4">
          <Aviso tono={enviado.hayCorreo ? 'exito' : 'info'}>{enviado.mensaje}</Aviso>
        </div>

        {!enviado.hayCorreo && (
          <div className="mt-4">
            <Aviso tono="info">
              <p className="font-medium">Este sistema todavía no tiene servidor de correo.</p>
              <p className="mt-1">
                Mientras tanto, el camino que funciona es pedirle a quien administra Núcleo que te
                genere un enlace desde <strong>Panel → Usuarios</strong> y te lo pase por mensaje.
              </p>
            </Aviso>
          </div>
        )}

        <Link
          to="/"
          className="mt-6 inline-flex h-11 w-full items-center justify-center rounded-md border border-line bg-surface text-sm font-medium hover:bg-surface-raised"
        >
          Volver al ingreso
        </Link>
      </MarcoPublico>
    );
  }

  return (
    <MarcoPublico>
      <h1 className="text-xl font-semibold tracking-tight">¿Olvidaste tu contraseña?</h1>
      <p className="mt-2 text-sm text-fg-muted">
        Escribí tu email y te mandamos un enlace para elegir una nueva.
      </p>

      {error !== null && (
        <div className="mt-4">
          <Aviso tono="error">{error}</Aviso>
        </div>
      )}

      <form
        onSubmit={(evento) => {
          void enviar(evento);
        }}
        className="mt-5 space-y-4"
      >
        <div>
          <label htmlFor="recuperar-email" className="mb-1.5 block text-sm font-medium">
            Email
          </label>
          <input
            id="recuperar-email"
            name="email"
            type="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
            autoFocus
            value={email}
            onChange={(evento) => {
              setEmail(evento.target.value);
            }}
            className="h-11 w-full rounded-md border border-line bg-surface px-3 outline-none transition-colors focus:border-accent"
          />
        </div>

        <Button
          type="submit"
          variant="primary"
          loading={enviando}
          disabled={email.trim() === '' || enviando}
          className="h-11 w-full"
        >
          {enviando ? 'Enviando…' : 'Enviarme el enlace'}
        </Button>
      </form>

      <Link
        to="/"
        className="mt-5 block text-center text-sm text-accent hover:underline"
      >
        Volver al ingreso
      </Link>
    </MarcoPublico>
  );
}
